import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Cloud, CloudOff, Database } from 'lucide-react';
import { useBookmarks } from '../contexts/BookmarkContext';
import { authService } from '../services/auth';
import { useProjects, useAllComponents } from '../hooks/useApi';
import { googleDriveService } from '../services/googleDrive';

import UnifiedTableView from '../components/UnifiedTableView';
import { bookmarkService } from '../services/bookmarks';

const BookmarksPage: React.FC = () => {
  const { bookmarks, refreshBookmarks } = useBookmarks();
  const [syncStatus, setSyncStatus] = useState<'synced' | 'local' | 'checking'>('checking');

  // Fetch all components and projects from the API
  const { data: allComponents, loading: componentsLoading, error: componentsError } = useAllComponents();
  const { data: allProjects, loading: projectsLoading, error: projectsError } = useProjects();

  const checkSyncStatus = async () => {
    try {
      const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
      setSyncStatus(hasDrivePermissions ? 'synced' : 'local');
    } catch (error) {
      setSyncStatus('local');
    }
  };

  useEffect(() => {
    checkSyncStatus();
  }, []);

  // Step 1: Load data from API (already done via DataContext)
  // Step 2: Get bookmarks (already done via BookmarkContext)
  // Step 3: Filter loaded data by bookmarks - ONLY API DATA, NO FALLBACKS
  const bookmarkedComponents = useMemo(() => {
    console.log('=== BOOKMARK FILTERING PROCESS - API ONLY ===');
    console.log('Step 1 - API Data loaded:', {
      allComponentsCount: allComponents?.length || 0,
      allProjectsCount: allProjects?.length || 0,
      sampleProjects: allProjects?.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        lastUpdated: p.lastUpdated,
        repository: p.repository
      }))
    });
    
    console.log('Step 2 - Bookmarks loaded:', {
      bookmarksCount: bookmarks?.length || 0,
      bookmarks: bookmarks?.map(b => ({ componentName: b.componentName, projectId: b.projectId }))
    });

    // Step 3: Filter loaded data by bookmarks - ONLY API DATA
    if (!allComponents || !bookmarks || !allProjects || allComponents.length === 0 || bookmarks.length === 0 || allProjects.length === 0) {
      console.log('Step 3 - Missing API data, returning empty array');
      return [];
    }

    // Create a map of bookmarks by component name
    const bookmarkMap = new Map(bookmarks.map(b => [b.componentName, b]));
    
    // Filter components and ONLY use API data
    const filtered = allComponents.filter(component => {
      const bookmark = bookmarkMap.get(component.name);
      const isBookmarked = !!bookmark;
      if (isBookmarked) {
        console.log(`✓ Component "${component.name}" is bookmarked`);
      }
      return isBookmarked;
    }).map(component => {
      const bookmark = bookmarkMap.get(component.name);
      if (!bookmark) return null;
      
      // Find the API project data - ONLY use exact matches
      const apiProject = allProjects.find(p => p.id === bookmark.projectId);
      
      if (!apiProject) {
        console.warn(`BookmarksPage: No API project found for bookmark projectId: ${bookmark.projectId}`);
        return null; // Skip this component - no API data available
      }
      
      console.log(`✓ Component "${component.name}" matched with API project:`, {
        projectId: apiProject.id,
        projectName: apiProject.name,
        lastUpdated: apiProject.lastUpdated,
        repository: apiProject.repository
      });
      
      // Return component with ONLY API data
      return {
        ...component,
        projectId: apiProject.id,
        projectName: apiProject.name,
        _projectMetadata: {
          id: apiProject.id,
          name: apiProject.name,
          description: apiProject.description,
          status: apiProject.status,
          lastUpdated: apiProject.lastUpdated,
          repository: apiProject.repository,
          patternlabUrl: apiProject.patternlabUrl
        }
      };
    }).filter((component): component is NonNullable<typeof component> => component !== null);

    console.log('Step 3 - Filtering complete:', {
      totalComponents: allComponents.length,
      totalBookmarks: bookmarks.length,
      filteredComponents: filtered.length,
      filteredComponentsList: filtered.map(c => ({ 
        name: c.name, 
        projectName: c.projectName, 
        projectId: c.projectId,
        status: c.status 
      }))
    });

    return filtered;
  }, [allComponents, bookmarks, allProjects]);

  // Clean up stale bookmarks when components are loaded
  useEffect(() => {
    if (allComponents && allComponents.length > 0 && bookmarks.length > 0) {
      const cleanupStaleBookmarks = async () => {
        try {
          const originalCount = bookmarks.length;
          await bookmarkService.cleanupStaleBookmarks(allComponents);
          const newCount = (await bookmarkService.getBookmarks()).length;
          const cleanedCount = originalCount - newCount;
          
          if (cleanedCount > 0) {
            console.log(`BookmarksPage: Cleaned up ${cleanedCount} stale bookmarks`);
          }
        } catch (error) {
          console.error('Error cleaning up stale bookmarks:', error);
        }
      };
      cleanupStaleBookmarks();
    }
  }, [allComponents, bookmarks.length]);

  if (!authService.isAuthenticated()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Bookmark className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Sign In Required</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            Please sign in to view your bookmarks
          </p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Show loading state while API data is being fetched
  if (componentsLoading || projectsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-300">Loading bookmarks and components...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Bookmarks Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-4xl font-bold gradient-text">
            Bookmarks
          </h1>
          {syncStatus !== 'checking' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-full">
              {syncStatus === 'synced' ? (
                <Cloud className="w-4 h-4 text-green-500" />
              ) : (
                <CloudOff className="w-4 h-4 text-slate-400" />
              )}
              <span className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                {syncStatus === 'synced' ? 'Cloud' : 'Local'}
              </span>
            </div>
          )}
        </div>
        <p className="text-slate-600 dark:text-slate-300 text-lg">
          Your saved components and projects
        </p>
      </div>





      {/* Error States */}
      {componentsError && (
        <div className="card mb-8">
          <div className="text-center text-red-600 dark:text-red-400">
            <h3 className="text-lg font-semibold mb-2">Error Loading Components</h3>
            <p>{componentsError}</p>
          </div>
        </div>
      )}

      {projectsError && (
        <div className="card mb-8">
          <div className="text-center text-red-600 dark:text-red-400">
            <h3 className="text-lg font-semibold mb-2">Error Loading Projects</h3>
            <p>{projectsError}</p>
          </div>
        </div>
      )}

      {/* Components Table */}
      {(componentsLoading || projectsLoading) && bookmarks.length > 0 ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            Loading your bookmarks...
          </h3>
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            Found {bookmarks.length} bookmarks, loading component data...
          </p>
        </div>
      ) : !componentsLoading && !projectsLoading && bookmarkedComponents.length === 0 ? (
        <div className="text-center py-12">
          <Database className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            No bookmarked components found
          </h3>
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            {bookmarks.length === 0 
              ? "You haven't bookmarked any components yet." 
              : "No components match your bookmarks. The bookmarked components may not exist in the current API data."
            }
          </p>
          {bookmarks.length === 0 && allComponents && allComponents.length > 0 && (
            <button
              onClick={async () => {
                try {
                  const testComponent = allComponents[0];
                  const testProject = allProjects?.find(p => p.id === testComponent.projectId);
                  await bookmarkService.addBookmark(testComponent, testProject);
                  window.alert(`Added test bookmark for ${testComponent.name}`);
                  window.location.reload();
                } catch (error) {
                  console.error('Failed to add test bookmark:', error);
                  window.alert('Failed to add test bookmark: ' + error);
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add Test Bookmark
            </button>
          )}
        </div>
      ) : bookmarkedComponents.length > 0 ? (
        <UnifiedTableView 
          showBookmarksOnly={true}
          bookmarkedComponents={bookmarkedComponents}
        />
      ) : null}

      {/* Step 4: Apply filtered data to table */}
      {(() => {
        console.log('Step 4 - Applying filtered data to table:', {
          showBookmarksOnly: true,
          bookmarkedComponentsCount: bookmarkedComponents.length,
          bookmarkedComponents: bookmarkedComponents.map(c => ({ name: c.name, projectName: c.projectName, status: c.status }))
        });
        return null;
      })()}
      

    </div>
  );
};

export default BookmarksPage;
